import { updateQuoteName } from "@/api/store/QuotesService.js";
import {
  getOrder,
  updateOrder,
  getOrderPaymentTypes
} from "@/api/store/OrdersService.js";
import {
  priceKeyOf,
  lineFor,
  emptyProductsUpdate,
  quoteResult,
  loadEditableQuote,
  generalInfoPayload,
  totalsPayload
} from "./quoteLines.js";

const sameCode = (a, b) =>
  String(a)
    .trim()
    .toUpperCase() ===
  String(b)
    .trim()
    .toUpperCase();

const normalize = text =>
  String(text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();

// null en el input = no tocar ese dato
const nullable = (type, description) => ({
  type: [type, "null"],
  description
});

const ADDRESS_FIELDS = [
  "name",
  "company",
  "street1",
  "street2",
  "city",
  "state",
  "postalCode",
  "country",
  "phone"
];

// El tipo de pago por código o nombre ("TCR", "credit card", "zelle"), entre
// los de la tienda que no están bloqueados
const findPaymentType = (types, wanted) => {
  const key = normalize(wanted);
  const usable = types.filter(type => String(type.blocked) !== "1");
  const found =
    usable.find(type => normalize(type.code) === key) ||
    usable.find(type => normalize(type.name) === key) ||
    usable.find(type => normalize(type.name).includes(key));
  if (!found) {
    throw new Error(
      `No hay un tipo de pago "${wanted}" en esta tienda. Los que hay: ${usable
        .map(type => `${type.name} (${type.code})`)
        .join(", ")}.`
    );
  }
  return found;
};

export default {
  definition: {
    name: "update_quote",
    description:
      "Modifica una cotización que ya existe; todo lo que no se pide queda como está (null). Puede: ponerle el cliente elegido en la tarjeta de clientes (recalcula los precios con su nivel y trae su dirección, tipo de pago y condición por defecto); cambiar cantidades (0 quita la línea), quitar piezas y agregar las elegidas en las tarjetas; cambiar el tipo de pago, la condición crédito/contado, la orden de compra, el VIN, el nombre, las notas, el descuento y la dirección de envío. El envío se pone con quote_shipping. Sólo cotizaciones creadas por el vendedor que te habla, nunca órdenes ni cotizaciones ya convertidas en orden. Devuelve la cotización completa como quedó guardada, con lo que le falta (missing); el sistema puede recortar cantidades al stock o quitar un descuento que el usuario no puede dar, y lo avisa en warnings.",
    strict: true,
    input_schema: {
      type: "object",
      properties: {
        reference: {
          type: "string",
          description:
            "La cotización, p. ej. 'Q-200000005'; vacío para la última creada o modificada en esta conversación"
        },
        setQuantities: {
          type: "array",
          description: "Nuevas cantidades por código NKR; 0 quita la línea",
          items: {
            type: "object",
            properties: {
              nkrCode: { type: "string" },
              quantity: { type: "integer" }
            },
            required: ["nkrCode", "quantity"],
            additionalProperties: false
          }
        },
        remove: {
          type: "array",
          items: { type: "string" },
          description: "Códigos NKR a quitar"
        },
        syncSelected: {
          type: "boolean",
          description:
            "true para dejar la cotización igual a lo elegido en las tarjetas: le pone el cliente elegido si lo hay, agrega las piezas elegidas que falten, les pone la cantidad elegida y quita las de las tarjetas que el vendedor desmarcó. Lo que se agregó a mano a la cotización y nunca salió en una tarjeta no se toca"
        },
        addSelected: {
          type: "boolean",
          description:
            "true para agregar las piezas elegidas en las tarjetas que no estén en la cotización"
        },
        setCustomer: {
          type: "boolean",
          description:
            "true para ponerle el cliente elegido en la tarjeta de clientes (find_customer)"
        },
        paymentType: nullable(
          "string",
          "Tipo de pago por código o nombre de los de la tienda (p. ej. 'Credit Card', 'Cash', 'Zelle'). 'Credit Card' es tarjeta de crédito, no la condición de crédito."
        ),
        // anyOf y no type: ["string", "null"]: la API rechaza un enum con
        // null en un tipo con varios valores
        paymentTerms: {
          anyOf: [
            { type: "string", enum: ["credit", "cash"] },
            { type: "null" }
          ],
          description:
            "Condición de pago: 'credit' (a crédito, CRED) o 'cash' (de contado, CONT)"
        },
        purchaseOrder: nullable(
          "string",
          "Número de orden de compra (PO) del cliente"
        ),
        vin: nullable("string", "VIN del vehículo"),
        name: nullable("string", "Nombre de la cotización"),
        internalNotes: nullable(
          "string",
          "Notas internas (no las ve el cliente); reemplaza las que haya"
        ),
        customerNotes: nullable(
          "string",
          "Notas para el cliente; reemplaza las que haya"
        ),
        discount: {
          type: ["object", "null"],
          description:
            "Descuento sobre las piezas: percentage (p. ej. 5 = 5%) o fixed (monto). value 0 lo quita",
          properties: {
            type: { type: "string", enum: ["percentage", "fixed"] },
            value: { type: "number" }
          },
          required: ["type", "value"],
          additionalProperties: false
        },
        shippingAddress: {
          type: ["object", "null"],
          description:
            "Dirección de envío; los campos en null quedan como están. Cambiarla obliga a recalcular el envío",
          properties: ADDRESS_FIELDS.reduce((acc, field) => {
            acc[field] = { type: ["string", "null"] };
            return acc;
          }, {}),
          required: ADDRESS_FIELDS,
          additionalProperties: false
        }
      },
      required: [
        "reference",
        "setQuantities",
        "remove",
        "syncSelected",
        "addSelected",
        "setCustomer",
        "paymentType",
        "paymentTerms",
        "purchaseOrder",
        "vin",
        "name",
        "internalNotes",
        "customerNotes",
        "discount",
        "shippingAddress"
      ],
      additionalProperties: false
    }
  },

  async run(input, ctx) {
    const selection = ctx.getSelection();
    let order = await loadEditableQuote(input.reference, ctx);
    const orderNumber = order.orderNumber;
    const warnings = [];
    const save = async (section, values) => {
      const res = await updateOrder(orderNumber, section, values);
      warnings.push(...((res && res.warnings) || []));
      order = await getOrder(orderNumber);
    };

    // 1. El cliente: recalcula los precios y trae sus defaults (dirección,
    // tipo de pago, condición), que lo pedido más abajo puede cambiar
    if (input.setCustomer || (input.syncSelected && selection.customer)) {
      if (!selection.customer) {
        throw new Error(
          "No hay cliente elegido: búscalo con find_customer y que el vendedor lo elija en la tarjeta."
        );
      }
      if (
        String(order.customerInfoautoId) !==
        String(selection.customer.personaId)
      ) {
        await save(
          "general-info",
          generalInfoPayload(order, {
            customerInfoautoId: selection.customer.personaId,
            customerEmail: selection.customer.email || ""
          })
        );
      }
    }

    // 2. Las piezas. Los kits se editan desde la orden
    const lines = (order.items || []).filter(item => !item.kitCode);
    const lineOf = code => lines.find(item => sameCode(item.nkrCode, code));
    const changes = emptyProductsUpdate();
    const notInQuote = [];
    const deleting = new Set();
    const deleteLine = line => {
      if (deleting.has(line.productId)) return;
      deleting.add(line.productId);
      changes.productsToDelete.push({
        productId: line.productId,
        nkrCode: line.nkrCode
      });
    };
    // Igual a lo elegido: la cantidad de cada pieza elegida (sumada si salió
    // en dos grupos) y fuera las de las tarjetas que ya no están elegidas
    const setQuantities = [...input.setQuantities];
    const remove = [...input.remove];
    if (input.syncSelected) {
      const chosen = new Map();
      selection.items.forEach(item => {
        const code = String(item.option.nkrCode).toUpperCase();
        chosen.set(code, (chosen.get(code) || 0) + item.quantity);
      });
      chosen.forEach((quantity, code) => {
        const line = lineOf(code);
        if (line && Number(line.quantity) !== quantity) {
          setQuantities.push({ nkrCode: line.nkrCode, quantity });
        }
      });
      lines
        .filter(line => {
          const code = String(line.nkrCode).toUpperCase();
          return selection.offeredCodes.has(code) && !chosen.has(code);
        })
        .forEach(line => remove.push(line.nkrCode));
    }
    remove.forEach(code => {
      const line = lineOf(code);
      if (line) deleteLine(line);
      else notInQuote.push(code);
    });
    setQuantities.forEach(({ nkrCode, quantity }) => {
      const line = lineOf(nkrCode);
      if (!line) return notInQuote.push(nkrCode);
      if (quantity <= 0) return deleteLine(line);
      if (deleting.has(line.productId)) return;
      // Mismo precio y bodega: el API vuelve a validar los dos
      changes.productsToUpdate.push({
        productId: line.productId,
        nkrCode: line.nkrCode,
        price: line.price,
        quantity,
        warehouseId: line.warehouseId,
        isTransfer: String(line.isTransfer) === "1"
      });
    });
    let skipped = [];
    if (input.addSelected || input.syncSelected) {
      const toAdd = selection.items.filter(
        item => !lineOf(item.option.nkrCode)
      );
      const priceKey = priceKeyOf(order);
      const departmentCode = order.departamentoCodigo || ctx.departmentCode;
      const results = await Promise.all(
        toAdd.map(item =>
          lineFor(item.option.nkrCode, item.quantity, priceKey, departmentCode)
        )
      );
      changes.productsToAdd = results.filter(r => r.line).map(r => r.line);
      skipped = results
        .map((result, index) => ({
          nkrCode: toAdd[index].option.nkrCode,
          reason: result.skipped
        }))
        .filter(result => result.reason);
    }
    const piecesChanged =
      changes.productsToAdd.length ||
      changes.productsToUpdate.length ||
      changes.productsToDelete.length;
    if (piecesChanged) await save("products-update", changes);

    // 3. Datos generales: orden de compra, VIN, condición
    const general = {};
    if (input.purchaseOrder !== null) {
      general.purchaseOrderNumber = input.purchaseOrder;
    }
    if (input.vin !== null) general.vin = input.vin;
    if (input.paymentTerms !== null) {
      general.creditRetail = input.paymentTerms === "credit" ? "CRED" : "CONT";
    }
    if (Object.keys(general).length) {
      await save("general-info", generalInfoPayload(order, general));
    }

    // 4. Dirección de envío: sólo los campos que vienen
    let addressChanged = false;
    if (input.shippingAddress) {
      const address = ADDRESS_FIELDS.reduce((acc, field) => {
        if (input.shippingAddress[field] !== null) {
          acc[field] = input.shippingAddress[field];
        }
        return acc;
      }, {});
      if (Object.keys(address).length) {
        await save("shipping-address", address);
        addressChanged = true;
      }
    }

    // 5. Totales: tipo de pago y descuento
    const totals = {};
    if (input.paymentType !== null) {
      const types = await getOrderPaymentTypes(ctx.collection);
      totals.paymentType = findPaymentType(types, input.paymentType).code;
    }
    if (input.discount) {
      const value = Math.max(0, Number(input.discount.value) || 0);
      Object.assign(
        totals,
        input.discount.type === "percentage"
          ? { discountType: "percentage", discountPercentage: value }
          : { discountType: "fixed", discount: value, discountPercentage: 0 }
      );
    }
    if (Object.keys(totals).length) {
      await save("totals", totalsPayload(order, totals));
    }

    // 6. Notas y nombre
    if (input.internalNotes !== null) {
      await save("notes", { notes: input.internalNotes });
    }
    if (input.customerNotes !== null) {
      await save("notes_customer", { notes: input.customerNotes });
    }
    if (input.name !== null) await updateQuoteName(orderNumber, input.name);

    // El envío se calculó con las piezas y la dirección de antes
    if (order.shippingPreference && (piecesChanged || addressChanged)) {
      warnings.push(
        "Las piezas o la dirección cambiaron después de calcular el envío: recalcúlalo con quote_shipping."
      );
    }
    return quoteResult(orderNumber, ctx, { skipped, warnings, notInQuote });
  }
};
